import { useEffect } from 'react';
import { useAsyncFn } from 'react-use';

import {
  QUERY_LIBRARY_PAGE_LIMIT,
  useLazyListQueryQuery,
  useListQueryQuery,
  type ListQueryApiResponse,
  type Query,
} from 'app/extensions/api/clients/queries/v1';

export type UseGetAllSavedQueriesResult = {
  /** The complete list, or undefined until every page has been drained. */
  data: Query[] | undefined;
  /** True during the initial load and during any background re-drain. */
  isFetching: boolean;
  error: unknown;
};

/**
 * Loads every saved query by following the Kubernetes list `continue` token until it is empty.
 *
 * Page 1 deliberately uses the normal (non-lazy) query rather than being drained like the rest:
 * `listQuery` provides the 'Query' tag that every saved-query mutation invalidates, so keeping a
 * real subscription for page 1 means a mutation anywhere in the app — including from Explore, where
 * this hook isn't mounted — refetches page 1, which in turn re-triggers the drain below. A fully
 * lazy drain would opt out of tag invalidation and force every mutation call site to refresh by
 * hand.
 *
 */
export function useGetAllSavedQueries(): UseGetAllSavedQueriesResult {
  const {
    data: firstPage,
    error: firstPageError,
    isFetching: isFetchingFirstPage,
    fulfilledTimeStamp,
  } = useListQueryQuery({ limit: QUERY_LIBRARY_PAGE_LIMIT }, { refetchOnMountOrArgChange: true });

  const [fetchPage] = useLazyListQueryQuery();

  const [drainState, drainRest] = useAsyncFn(
    async (first: ListQueryApiResponse): Promise<Query[]> => {
      const all: Query[] = [...(first.items ?? [])];
      let continueToken = first.metadata?.continue;

      while (continueToken) {
        const page = await fetchPage({ limit: QUERY_LIBRARY_PAGE_LIMIT, continue: continueToken }).unwrap();
        all.push(...(page.items ?? []));

        const nextToken = page.metadata?.continue;
        if (nextToken === continueToken) {
          break;
        }
        continueToken = nextToken;
      }

      return all;
    },
    [fetchPage]
  );

  useEffect(() => {
    // Bail on firstPageError too: a failed refetch leaves the previous page-1 `data` in the cache
    // and only flips isFetching to false, so without this guard we would re-drain from stale
    // continue tokens after a page-1 error.
    if (!firstPage || isFetchingFirstPage || firstPageError) {
      return;
    }
    drainRest(firstPage);
    // fulfilledTimeStamp is what makes a re-drain happen at all: with structural sharing, a refetch
    // returning byte-identical page-1 content keeps the same object reference, so deleting a query
    // that lives on a later page would never re-drain if this keyed on `firstPage` alone.
  }, [firstPage, fulfilledTimeStamp, isFetchingFirstPage, firstPageError, drainRest]);

  return {
    data: drainState.value,
    isFetching: isFetchingFirstPage || (drainState.loading && !firstPageError),
    error: firstPageError ?? drainState.error,
  };
}
